import { css } from '@emotion/react'
import { ConceptChipDomain } from '@mathflat/domain/@entities/ConceptChip/domain.ts'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { isMobile } from 'react-device-detect'

import { useRepository } from '~/@common/hooks/useRepository'
import { commonRepo } from '~/@common/services/repo.service.ts'
import { typo } from '~/@common/styles'
import { Icon } from '~/@common/ui/Icon/Icon'
import ConceptChipAchievementLevel from '~/@pages/@common/(ConceptChip)/ConceptChipAchievmentLevel/ConceptChipAchievementLevel.tsx'
import { ChallengeStore } from '~/@pages/student/@common/store/Challenge.store.ts'
import ChallengeStartDrawer from '~/@pages/student/challenge/@widget/ChallengeStartDrawer/ChallengeStartDrawer.tsx'

import ConceptualLearningStatus from '../ConceptualLearningStatus/ConceptualLearningStatus'
import AssignedChallengeService from './AssignedChallenge.service'

const AssignedChallengeContents = () => {
  const service = useRepository(AssignedChallengeService)
  const challengeStore = useRepository(ChallengeStore)

  const getAchievementLevel = (conceptChipId: number) => {
    return (
      service.achievementMap.get(conceptChipId) ?? ConceptChipDomain.LEVEL_OF_ACHIEVEMENT.풀지않음
    )
  }

  const getFirstAssignedConceptChip = () => {
    return (
      Object.values(service.assignedConceptChipByType).find((items) => items.length > 0)?.[0] ??
      null
    )
  }
  const firstAssignedConceptChip = getFirstAssignedConceptChip()

  const handleClickConceptChipChallenge = ({
    conceptChipId,
    middleChapterId,
  }: {
    conceptChipId: number
    middleChapterId: number
  }) => {
    challengeStore.setSelectedChallenge({ type: 'conceptChip', id: conceptChipId, middleChapterId })
  }

  const handleClickLittleChapterChallenge = () => {
    if (!firstAssignedConceptChip) return
    challengeStore.setSelectedChallenge({
      type: 'littleChapter',
      id: firstAssignedConceptChip.littleChapterId,
      middleChapterId: firstAssignedConceptChip.middleChapterId,
    })
  }
  const isLittleChapterSelected = (() => {
    if (!firstAssignedConceptChip) return false
    return (
      challengeStore.selectedChallenge?.type === 'littleChapter' &&
      challengeStore.selectedChallenge.id === firstAssignedConceptChip.littleChapterId
    )
  })()
  const studentId = commonRepo.studentId

  useEffect(() => {
    if (!studentId) return
    service.fetchAssignedChallenges({ studentId })
  }, [service, studentId])

  return (
    <div css={S.container}>
      <div className="guide">
        <Icon name="icon_info_fill" size={16} color="var(--Colors-Purple-400)" />
        선생님이 수업으로 설정해준 챌린지 화면이에요!
      </div>
      {service.isEmptyAssignedChallenge ? (
        <div css={S.empty}>
          수업 챌린지가 없어요.
          <br />
          선생님께 챌린지 출제를 요청해요!
        </div>
      ) : (
        <div css={S.contents}>
          <div css={S.contentsHeader}>
            {service.assignedConceptChipInfo.bigChapterName} ㅣ&nbsp;
            {service.assignedConceptChipInfo.middleChapterName}
          </div>
          <div css={S.contentsBody}>
            <div className="little-chapter-container">
              <div className="little-chapter-title">
                {service.assignedConceptChipInfo.littleChapterName}
              </div>
              <button
                className={clsx({ selected: isLittleChapterSelected })}
                onClick={handleClickLittleChapterChallenge}
              >
                취약유형 추천 학습
              </button>
            </div>
            <div className="concept-chip-container">
              <ConceptualLearningStatus
                littleChapterId={service.assignedConceptChipInfo.littleChapterId}
                isMobile={isMobile}
              />
              {ConceptChipDomain.ORDERED_LEVEL_OF_DIFFICULTY.map((level) => {
                const conceptChipsByType = service.assignedConceptChipByType[level]
                return (
                  <div className="difficulty-level-container" key={level}>
                    <div className="difficulty-level-title">
                      <b>{level}</b>
                      <span>{conceptChipsByType.length}개</span>
                    </div>
                    <div className="difficulty-level-items">
                      {conceptChipsByType.map(({ conceptChipId, recommended, middleChapterId }) => {
                        const isChecked =
                          challengeStore.selectedChallenge?.type === 'conceptChip' &&
                          challengeStore.selectedChallenge.id === conceptChipId
                        return (
                          <ConceptChipAchievementLevel
                            key={conceptChipId}
                            type="checkbox"
                            level={getAchievementLevel(conceptChipId)}
                            checked={isChecked}
                            onChange={() =>
                              handleClickConceptChipChallenge({ conceptChipId, middleChapterId })
                            }
                            recommended={recommended}
                            size={30}
                          />
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
      <ChallengeStartDrawer />
    </div>
  )
}

export default observer(AssignedChallengeContents)

const S = {
  container: css`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background-color: var(--White);
    width: 100%;
    height: 100%;
    overflow: hidden;

    .guide {
      ${typo.body02}
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      gap: 4px;
      color: var(--Colors-Purple-500);
      font-weight: 700;
      padding: 14px;
      border-radius: var(--Radius-300);
      border: 1px solid var(--Colors-Purple-200);
      background: var(--Colors-Purple-50);
    }
  `,
  empty: css`
    ${typo.heading05}
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--Colors-Ash-600);
    text-align: center;
    font-weight: 700;
    height: 100%;
    width: 100%;
  `,
  contents: css`
    display: flex;
    flex-direction: column;
  `,
  contentsHeader: css`
    ${typo.body01}
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 18px;
    background: #e1e6eb;
    color: var(--Colors-Gray-800);
    font-weight: 700;
    border-radius: var(--Radius-300) var(--Radius-300) 0 0;
  `,
  contentsBody: css`
    display: flex;
    flex-direction: column;
    border: 1px solid var(--Colors-Gray-300);
    border-radius: 0 0 var(--Radius-300) var(--Radius-300);
    overflow: hidden;

    .little-chapter-container {
      display: flex;
      padding: 10px 12px 10px 16px;
      justify-content: space-between;
      align-items: center;
      align-self: stretch;
      background: var(--White);
      border-bottom: 1px solid var(--Colors-Gray-300);
    }

    .little-chapter-title {
      ${typo.body01}
      color: var(--Colors-Gray-900);
      text-overflow: ellipsis;
    }

    .concept-chip-container {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      background: var(--White);
    }

    .difficulty-level-container + .difficulty-level-container {
      border-left: 1px solid var(--Colors-Gray-300);
    }

    .difficulty-level-title {
      padding: 16px 12px;
    }

    .difficulty-level-title b {
      margin-right: 4px;
      font-weight: 700;
    }

    .difficulty-level-items {
      display: grid;
      grid-template-columns: repeat(auto-fit, 30px);
      grid-gap: 6px;
      grid-auto-flow: row;
      padding: 0 12px 12px 12px;
    }

    button {
      display: flex;
      padding: 10px 24px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      align-self: stretch;
      border-radius: 10px;
      background: var(--Colors-Gray-300);
      color: var(--Colors-Gray-800);

      ${typo.body02}
      font-weight: 700;

      &.selected {
        background: var(--Colors-Gray-500);
      }
    }
  `,
}
